import { useState } from "react";
import { IoClose } from "react-icons/io5";
import toast from "react-hot-toast";
import { useAuthStore } from "../../context/useAuthStore";
import { ROUTES } from "../../constant/routes";
import { Link } from "react-router-dom";

interface TCStatusModalProps {
  showModal: boolean;
  onClose: () => void;
}

const TCStatusModal: React.FC<TCStatusModalProps> = ({
  showModal,
  onClose,
}) => {
  // ✅ Use 'true' | 'false' for tcStatus
  const [accepted, setAccepted] = useState<"true" | "false">("false");
  const [isLoading, setIsLoading] = useState(false);

  const { user, token, updateUser, clearSession } = useAuthStore();

  if (!showModal) return null;

  const handleAccept = async () => {
    if (!user?.userId || !token) {
      toast.error("User information not found. Please log in again.");
      return;
    }

    if (accepted !== "true") {
      toast.error("Please accept the Terms & Conditions to continue.");
      return;
    }

    setIsLoading(true);

    try {
      const updatedData = { tcStatus: "true" };

      // Call backend to update
      await updateUser(updatedData);

      // ✅ Update Zustand store and sessionStorage together
      const updatedUser = { ...user, tcStatus: "true" };
      updateUser(updatedUser);
      sessionStorage.setItem("user", JSON.stringify(updatedUser));

      toast.success("Terms & Conditions accepted successfully!");

      setAccepted("false");
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update profile, please try again.";

      if (
        error?.response?.status === 401 ||
        message.toLowerCase().includes("session expired")
      ) {
        clearSession("expired");
        return;
      }

      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setAccepted("false");
    onClose();
  };

  return (
    <div className="modalSection">
      <div className="modalContainer">
        {/* Header */}
        <div className="flex justify-between items-center gap-2 border-b pb-1 border-gray-200 mb-2">
          <h2 className="text-lg font-semibold">Terms & Conditions</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
            disabled={isLoading}
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="mb-4">
          <ul className="list-disc pl-5 space-y-2">
            <li className="text-left">
              Read our{" "}
              <Link
                to={ROUTES.TERMS_AND_CONDITIONS}
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-800 underline transition-colors duration-200"
              >
                Terms & Conditions
              </Link>{" "}
              carefully.
            </li>
            <li className="text-left">
              By accepting, you agree to comply with all rules, policies, and
              guidelines.
            </li>
            <li className="text-left">
              You acknowledge that you have read and understood our privacy
              policy and usage terms.
            </li>
          </ul>
        </div>

        {/* Checkbox */}
        <div className="flex items-center gap-3 mb-6">
          <input
            type="checkbox"
            id="acceptTC"
            checked={accepted === "true"}
            onChange={(e) => setAccepted(e.target.checked ? "true" : "false")}
            className="w-5 h-5 text-gray-600 rounded focus:ring-gray-500"
            disabled={isLoading}
          />
          <label htmlFor="acceptTC" className="text-gray-700 font-medium">
            I agree to the Terms & Conditions
          </label>
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleAccept}
            disabled={accepted !== "true" || isLoading}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
              accepted !== "true" || isLoading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-gray-600 text-white hover:bg-gray-700"
            }`}
          >
            {isLoading ? "Accepting..." : "Accept"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TCStatusModal;
